import { ReactNode } from 'react';
import { TValidatorSpec } from './parseValidators';
import { TValidatorFn } from './validators';

const PLACEHOLDER_REGEX = /{(\d+)}/g;

type TValidatorHash<D> = Record<string, TValidatorSpec<D>[] | TValidatorFn<D>[]>;

export interface IUserConfig {
  stopOnFirstError?: boolean;
}

let config: IUserConfig = {};

const validationProcessor = {
  IGNORE_ALL_OTHER_VALIDATORS: 'IGNORE_ALL_OTHER_VALIDATORS',
  /**
   * If error message is no a string return it as it is.
   * Performs parameterized replacement of tokens within the message with the message arguments.
   * The tokens must be numbered and will be replaced by the message arguments by index.
   * Thus {0} gets the first message argument and {1} get the second.
   *
   * For example "{0} is required" with arguments ['Name'] would become "Name is required"
   *
   * msg - The message to perform token formatting on.
   * msgArgs - The arguments to use to replace the tokens with, in order.
   */
  formatMsgTokens(msg: ReactNode, msgArgs: string[]) {
    if (typeof msg === 'string') {
      return msg.replace(PLACEHOLDER_REGEX, (match, index) =>
        typeof msgArgs[index] === 'undefined' ? match : msgArgs[index]
      );
    }
    return msg;
  },
  /**
   * Formats a validator message by assembling its name and parameters and doing token replacement
   * via formatMsgTokens.
   *
   * validator - The validator whose message we wish to format
   * customMsg - Optional custom message or component to use in place of
   *   the one found on the validator. Used for custom validator functions which return their
   *   message or component in the case of validation failure.
   * data - The data object whose validation messages we are formatting
   */
  formatValidatorMsg<D = any>(validator: TValidatorSpec<D>, customMsg?: string | null, data?: D) {
    // name can be a property that is the name OR a function that returns it to us
    let msgParams = [typeof validator.name === 'function' ? validator.name(data) : validator.name!];

    // All parameters contained in param become message parameters
    // This works with single parameters as well as arrays of params, but not objects
    if (typeof validator.param !== 'undefined' && validator.param !== true) {
      msgParams =
        typeof validator.param === 'function'
          ? msgParams.concat(validator.param(data))
          : msgParams.concat(validator.param as string);
    }

    // TODO need to add the ability to add msgParams to a validator for when we need explicit
    //   parameters for a message; also we will eventually need support for named params

    return this.formatMsgTokens(customMsg || validator.msg, msgParams);
  },
  /**
   * Runs a single validator function on a single attribute of the data and returns a formatted
   * error message or component if it fails validation and undefined if it passes.
   *  validator - The validator object to run against the attribute.
   *  key - The key of the attribute on the data to validate.
   *  data - The data to validate
   */
  runValidator<D = any>(validator: TValidatorSpec, key: keyof D | string, data: D) {
    const param = typeof validator.param === 'function' ? validator.param(data) : validator.param;

    return new Promise((resolve, reject) => {
      const result: any = validator.fn?.(key, param, data);

      (result instanceof Promise ? result : Promise.resolve(result))
        .then((validationResult) => {
          /* TODO consider revising how we treat the return values of custom validators as it MUST be
           *  exactly undefined to specify a pass or true exactly and this can be a bit hard to
           *  understand perhaps throw an exception or log something? */

          /* built in validators return true or false to specify a passed, or failed validation.
           * custom ones defined in the validator spec will return an error message if they fail and
           * nothing (undefined) if they pass thus we need to be prepared for both types of result
           * additionally we have a special result that can be used to stop all other validators for
           * an attribute */
          if (
            validationResult !== true &&
            !(validationResult === null || typeof validationResult === 'undefined') &&
            this.IGNORE_ALL_OTHER_VALIDATORS !== validationResult
          ) {
            // eslint-disable-next-line no-param-reassign
            validationResult = this.formatValidatorMsg(validator, validationResult, data);
          }

          // return an error message if we have one otherwise nothing
          resolve(validationResult === true ? undefined : validationResult);
        })
        .catch(reject);
    }).catch((error) => {
      console.error(error); // eslint-disable-line no-console
      return undefined;
    });
  },
  /**
   * Runs a group of validators on the data and returns the errors if present
   *
   * data - The data to validate
   * validatorHash - A hash of validators for the data as retrieved by
   *   getValidators or equivalent.
   */
  runValidators<D = any>(data: D, validatorHash: TValidatorHash<D>) {
    const warnings: { [key: string]: ReactNode[] } = {};

    const reduceMessages = (messages: unknown[], key: string) =>
      messages.reduce((ignore, message) => {
        if (ignore || message === this.IGNORE_ALL_OTHER_VALIDATORS) {
          return true;
        }

        if (message !== null && typeof message !== 'undefined') {
          warnings[key] = warnings[key] || [];
          warnings[key].push(message as ReactNode);

          if (config.stopOnFirstError) {
            return true;
          }
        }

        return false;
      }, false);

    return new Promise((resolve) => {
      Promise.all(
        Object.keys(validatorHash).map((key) => {
          const validatorSet = validatorHash[key];

          return Promise.all(
            validatorSet.map((validator) => this.runValidator(validator, key, data))
          ).then((messages) => reduceMessages(messages, key));
        })
      ).then(() => resolve(Object.keys(warnings).length ? warnings : null));
    });
  },

  setConfig(userConfig: IUserConfig) {
    config = userConfig;
  },
};

export default validationProcessor;
